/**
 * Payment method reducer
 */
import { produce } from 'immer';

import { FAILED, LOADING, NONE, SUCCEEDED } from '../../constants/Status';

import * as t from './actionTypes';

/**
 * @typedef {Object} Category
 * @property {string} id
 * @property {string} label
 * @property {string} translation_key
 *
 * @typedef {Object} State
 * @property {Object<string, Category>} entities
 * @property {number} count
 * @property {string} status
 * @property {Error} [error]
 */

/**
 * @type {State}
 */
const initialState = {
  entities: {},
  count: 0,
  status: NONE,
  error: null,
};

/**
 * Payment method root reducer
 *
 * @param {State} state initial or previous state of data
 * @param {Object} action the action object
 *
 * @return {Object}
 */
export default function root(state = initialState, action = {}) {
  switch (action.type) {
    case t.CATEGORIES_REQUESTED:
      // Mark the state as "loading" so we can show a spinner or something.
      // Also, reset any errors. We're starting fresh.
      return produce(state, draft => {
        draft.status = LOADING;
        draft.error = null;
      });
    case t.CATEGORIES_SUCCEEDED:
      // All done: set loading to "false".
      // Also, replace the items with the ones from the server
      return produce(state, draft => {
        draft.status = SUCCEEDED;
        draft.error = null;
        for (const category of action.payload.categories) {
          draft.entities[category.label] = {
            ...draft.entities[category.label],
            ...category,
          };
        }
        draft.count = action.payload.categories.length;
      });
    case t.CATEGORIES_FAILED:
      // The request failed, but it did stop, so set loading to "false".
      // Save the error, and we can display it somewhere.
      return produce(state, draft => {
        draft.status = FAILED;
        draft.error = action.payload;
      });
    default:
      return state;
  }
}
